"use client";

import { VIEWER_STACK_MAX_WIDTH } from "@/config/layout";
import type { ViewerLayout } from "@/hooks/use-viewer-layout";
import { cn } from "@/lib/utils";
import { badgeTitleOf } from "@/utils/page-anchor";
import { hasCommentSection } from "@/utils/slide-kinds";
import type { SplitPage } from "@/utils/splitPages";
import type { ReactNode } from "react";
import { HandyoutBadge } from "./handyout-badge";

export function ViewerSplitShell({
  layout,
  moduleColumnWidth,
  detail,
  children,
}: {
  layout: ViewerLayout;
  moduleColumnWidth: number;
  detail?: ReactNode;
  children: ReactNode;
}) {
  const split = layout === "split" && moduleColumnWidth > 0;
  return (
    <div className="flex h-full w-full">
      <div
        data-viewer-module-column=""
        className={cn(
          "relative h-full min-w-0",
          split ? "shrink-0" : "mx-auto w-full",
        )}
        style={
          split
            ? { width: moduleColumnWidth }
            : { maxWidth: VIEWER_STACK_MAX_WIDTH }
        }
      >
        {children}
      </div>
      {split ? (
        <div className="relative h-full min-w-0 flex-1 overflow-hidden">
          {detail}
        </div>
      ) : null}
    </div>
  );
}

export function ViewerChrome({
  page,
  pageNumber,
  totalPages,
  actions,
}: {
  page: SplitPage | undefined;
  pageNumber: number;
  totalPages: number;
  actions?: ReactNode;
}) {
  return (
    <>
      <div className="pointer-events-none absolute left-5 top-6 z-10">
        <HandyoutBadge title={badgeTitleOf(page)} />
      </div>
      {actions ? (
        <div className="absolute right-5 top-6 z-10 flex h-6 items-center gap-1">
          {actions}
        </div>
      ) : null}
      <div
        className={cn(
          "pointer-events-none absolute left-0 right-0 z-10 flex flex-row items-center justify-between pb-1 pl-4 pr-6",
          hasCommentSection(page?.slideId) ? "bottom-14" : "bottom-6",
        )}
      >
        <span className="text-xs font-normal text-primary">©handyout</span>
        <span className="text-xs font-normal tabular-nums text-muted-foreground">
          {pageNumber}/{totalPages}
        </span>
      </div>
    </>
  );
}
